
import { useState, useRef, useMemo } from 'react';
import type { FinanceRecord } from '../../../store/financeStore';

const OPENING_STOCK: Record<string, number> = {
  小麦: 1200, 玉米: 2500, 稻谷: 800, 大米: 600, 大豆: 300, 高粱: 150,
};

interface Props {
  open: boolean;
  onClose: () => void;
  records: FinanceRecord[];
}

export default function MonthlyReportModal({ open, onClose, records }: Props) {
  const [selectedMonth, setSelectedMonth] = useState('2026-02');
  const [exportingPDF, setExportingPDF] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const monthData = useMemo(() => {
    const [year, month] = selectedMonth.split('-');
    const prefix = `${year}-${month}`;

    const varietyMap: Record<string, {
      purchaseQty: number; purchaseAmt: number;
      salesQty: number; salesAmt: number;
      cost: number; tax: number; subsidy: number;
      avgPurchasePrice: number; avgSalesPrice: number;
      purchaseCount: number; salesCount: number;
    }> = {};

    records.forEach(r => {
      if (!r.date.startsWith(prefix)) return;
      if (!varietyMap[r.variety]) {
        varietyMap[r.variety] = {
          purchaseQty: 0, purchaseAmt: 0, salesQty: 0, salesAmt: 0,
          cost: 0, tax: 0, subsidy: 0, avgPurchasePrice: 0, avgSalesPrice: 0,
          purchaseCount: 0, salesCount: 0,
        };
      }
      const v = varietyMap[r.variety];
      if (r.bizType === '收购') {
        v.purchaseQty += r.quantity;
        v.purchaseAmt += r.amount;
        v.cost += r.operatingCost || r.amount;
        v.subsidy += r.subsidyAmount || r.subsidy || 0;
        v.purchaseCount++;
      } else {
        v.salesQty += r.quantity;
        v.salesAmt += r.amount;
        v.tax += r.taxAmount || r.tax || 0;
        v.salesCount++;
      }
    });

    const rows = Object.entries(varietyMap).map(([variety, data]) => {
      const opening = OPENING_STOCK[variety] ?? 0;
      const closing = Math.max(0, opening + data.purchaseQty - data.salesQty);
      return {
        variety, opening, closing,
        ...data,
        avgPurchasePrice: data.purchaseCount > 0 ? Math.round(data.purchaseAmt / data.purchaseQty) : 0,
        avgSalesPrice: data.salesCount > 0 ? Math.round(data.salesAmt / data.salesQty) : 0,
        grossProfit: data.salesAmt - data.cost,
      };
    });

    const totals = rows.reduce((acc, r) => ({
      opening: acc.opening + r.opening,
      closing: acc.closing + r.closing,
      purchaseQty: acc.purchaseQty + r.purchaseQty,
      purchaseAmt: acc.purchaseAmt + r.purchaseAmt,
      salesQty: acc.salesQty + r.salesQty,
      salesAmt: acc.salesAmt + r.salesAmt,
      cost: acc.cost + r.cost,
      tax: acc.tax + r.tax,
      subsidy: acc.subsidy + r.subsidy,
      grossProfit: acc.grossProfit + r.grossProfit,
    }), { opening: 0, closing: 0, purchaseQty: 0, purchaseAmt: 0, salesQty: 0, salesAmt: 0, cost: 0, tax: 0, subsidy: 0, grossProfit: 0 });

    return { rows, totals, year, month };
  }, [records, selectedMonth]);

  const handleExportPDF = async () => {
    if (!contentRef.current) return;
    setExportingPDF(true);
    try {
      const { default: html2canvas } = await import('html2canvas');
      const { jsPDF } = await import('jspdf');
      const canvas = await html2canvas(contentRef.current, { scale: 2, useCORS: true, logging: false, backgroundColor: '#ffffff' });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('l', 'mm', 'a4');
      const pdfW = pdf.internal.pageSize.getWidth();
      const pdfH = pdf.internal.pageSize.getHeight();
      const imgW = pdfW - 20;
      const imgH = (canvas.height * imgW) / canvas.width;
      let hLeft = imgH;
      let pos = 10;
      pdf.addImage(imgData, 'PNG', 10, pos, imgW, imgH);
      hLeft -= pdfH - 20;
      while (hLeft > 0) {
        pos = hLeft - imgH + 10;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 10, pos, imgW, imgH);
        hLeft -= pdfH - 20;
      }
      pdf.save(`购销存月报表_${selectedMonth}.pdf`);
    } catch { window.print(); } finally { setExportingPDF(false); }
  };

  if (!open) return null;

  const monthLabel = `${monthData.year}年${parseInt(monthData.month, 10)}月`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-[1100px] max-h-[92vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        {/* 头部 */}
        <div className="flex items-center justify-between px-8 pt-6 pb-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-teal-50 rounded-xl flex items-center justify-center">
              <i className="ri-file-excel-2-line text-xl text-teal-600"></i>
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-800">购销存月报表</h2>
              <p className="text-xs text-gray-500 mt-0.5">符合国家粮储局标准格式 · 按品种汇总月度购销存数据</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <input type="month" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 focus:ring-2 focus:ring-teal-400 focus:border-transparent cursor-pointer" />
            <button onClick={handleExportPDF} disabled={exportingPDF}
              className="px-4 py-2 bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-lg hover:shadow-md transition-all text-sm font-medium whitespace-nowrap cursor-pointer disabled:opacity-60 flex items-center">
              {exportingPDF ? <><i className="ri-loader-4-line mr-1.5 animate-spin"></i>导出中...</> : <><i className="ri-file-pdf-2-line mr-1.5"></i>导出PDF</>}
            </button>
            <button onClick={() => window.print()} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer flex items-center">
              <i className="ri-printer-line mr-1.5"></i>打印
            </button>
            <button onClick={onClose} className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer text-gray-400">
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
        </div>

        {/* 内容 */}
        <div className="flex-1 overflow-y-auto px-8 py-6">
          <div ref={contentRef} className="space-y-6">
            <div className="text-center border-b-2 border-gray-800 pb-5">
              <h1 className="text-2xl font-bold text-gray-900 tracking-wider mb-2">粮食购销存月报表</h1>
              <div className="flex items-center justify-center space-x-6 text-sm text-gray-600 mt-3">
                <span>企业名称：<strong className="text-gray-800">丰收粮食贸易有限公司</strong></span>
                <span className="text-gray-300">|</span>
                <span>统计月份：<strong className="text-gray-800">{monthLabel}</strong></span>
                <span className="text-gray-300">|</span>
                <span>填报日期：<strong className="text-gray-800">{new Date().toLocaleDateString('zh-CN')}</strong></span>
              </div>
            </div>

            {/* 汇总指标 */}
            <div className="grid grid-cols-5 gap-3">
              {[
                { label: '期初库存', value: `${monthData.totals.opening.toLocaleString()} 吨`, color: 'bg-gray-50 border-gray-200', valColor: 'text-gray-800' },
                { label: '本月收购', value: `${monthData.totals.purchaseQty.toLocaleString()} 吨`, color: 'bg-teal-50 border-teal-200', valColor: 'text-teal-700' },
                { label: '本月销售', value: `${monthData.totals.salesQty.toLocaleString()} 吨`, color: 'bg-orange-50 border-orange-200', valColor: 'text-orange-600' },
                { label: '期末库存', value: `${monthData.totals.closing.toLocaleString()} 吨`, color: 'bg-amber-50 border-amber-200', valColor: 'text-amber-700' },
                { label: '毛利润', value: `¥${monthData.totals.grossProfit.toLocaleString()}`, color: 'bg-emerald-50 border-emerald-200', valColor: 'text-emerald-700' },
              ].map(item => (
                <div key={item.label} className={`${item.color} border rounded-xl p-3 text-center`}>
                  <div className="text-xs text-gray-500 mb-1">{item.label}</div>
                  <div className={`text-lg font-bold ${item.valColor}`}>{item.value}</div>
                </div>
              ))}
            </div>

            {/* 主表 */}
            {monthData.rows.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-gray-300 bg-gray-50 rounded-xl border border-gray-100">
                <i className="ri-inbox-line text-3xl mb-2"></i>
                <p className="text-sm text-gray-400">{monthLabel} 暂无经营数据</p>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-gray-100">
                      {['粮食品种','期初库存(吨)','收购量(吨)','收购金额(元)','均价(元/吨)','销售量(吨)','营业收入(元)','均价(元/吨)','营业成本(元)','税款(元)','补贴(元)','期末库存(吨)','毛利润(元)'].map(h => (
                        <th key={h} className="border-b border-gray-200 px-3 py-3 text-gray-700 font-bold whitespace-nowrap text-center">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {monthData.rows.map((row, idx) => (
                      <tr key={row.variety} className={`border-b border-gray-100 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'} hover:bg-teal-50/20 transition-colors`}>
                        <td className="px-3 py-2.5 text-center font-semibold text-gray-800">{row.variety}</td>
                        <td className="px-3 py-2.5 text-right text-gray-600">{row.opening.toLocaleString()}</td>
                        <td className="px-3 py-2.5 text-right text-teal-600 font-medium">{row.purchaseQty.toLocaleString()}</td>
                        <td className="px-3 py-2.5 text-right text-gray-700">{row.purchaseAmt.toLocaleString()}</td>
                        <td className="px-3 py-2.5 text-right text-gray-500">{row.avgPurchasePrice > 0 ? row.avgPurchasePrice.toLocaleString() : '—'}</td>
                        <td className="px-3 py-2.5 text-right text-orange-500 font-medium">{row.salesQty.toLocaleString()}</td>
                        <td className="px-3 py-2.5 text-right text-emerald-600 font-medium">{row.salesAmt.toLocaleString()}</td>
                        <td className="px-3 py-2.5 text-right text-gray-500">{row.avgSalesPrice > 0 ? row.avgSalesPrice.toLocaleString() : '—'}</td>
                        <td className="px-3 py-2.5 text-right text-gray-700">{row.cost.toLocaleString()}</td>
                        <td className="px-3 py-2.5 text-right text-amber-600">{row.tax.toLocaleString()}</td>
                        <td className="px-3 py-2.5 text-right text-teal-600">{row.subsidy.toLocaleString()}</td>
                        <td className="px-3 py-2.5 text-right font-bold text-gray-800">{row.closing.toLocaleString()}</td>
                        <td className={`px-3 py-2.5 text-right font-semibold ${row.grossProfit >= 0 ? 'text-emerald-700' : 'text-rose-600'}`}>
                          {row.grossProfit >= 0 ? '+' : ''}{row.grossProfit.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-100 font-bold border-t-2 border-gray-300">
                      <td className="px-3 py-3 text-center text-gray-800">合计</td>
                      <td className="px-3 py-3 text-right text-gray-600">{monthData.totals.opening.toLocaleString()}</td>
                      <td className="px-3 py-3 text-right text-teal-700">{monthData.totals.purchaseQty.toLocaleString()}</td>
                      <td className="px-3 py-3 text-right text-gray-800">{monthData.totals.purchaseAmt.toLocaleString()}</td>
                      <td className="px-3 py-3 text-center text-gray-400">—</td>
                      <td className="px-3 py-3 text-right text-orange-600">{monthData.totals.salesQty.toLocaleString()}</td>
                      <td className="px-3 py-3 text-right text-emerald-700">{monthData.totals.salesAmt.toLocaleString()}</td>
                      <td className="px-3 py-3 text-center text-gray-400">—</td>
                      <td className="px-3 py-3 text-right text-gray-800">{monthData.totals.cost.toLocaleString()}</td>
                      <td className="px-3 py-3 text-right text-amber-700">{monthData.totals.tax.toLocaleString()}</td>
                      <td className="px-3 py-3 text-right text-teal-700">{monthData.totals.subsidy.toLocaleString()}</td>
                      <td className="px-3 py-3 text-right text-gray-800">{monthData.totals.closing.toLocaleString()}</td>
                      <td className={`px-3 py-3 text-right ${monthData.totals.grossProfit >= 0 ? 'text-emerald-700' : 'text-rose-600'}`}>
                        {monthData.totals.grossProfit >= 0 ? '+' : ''}{monthData.totals.grossProfit.toLocaleString()}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            {/* 保密声明 */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-xs text-amber-800 leading-relaxed">
              <i className="ri-shield-keyhole-line mr-1"></i>
              <strong>保密声明：</strong>本报表数据涉及商业秘密，严格按照相关保密规定执行。报送的基本数据须与生产经营实际相符，与粮食经营台账相符。
            </div>

            {/* 签章 */}
            <div className="border-t-2 border-gray-800 pt-5 mt-2">
              <div className="grid grid-cols-3 gap-8 text-sm text-gray-700">
                <div><div className="font-medium text-gray-800 mb-5">制表人（签字）：</div><div className="border-b border-gray-400 pb-1"></div></div>
                <div><div className="font-medium text-gray-800 mb-5">企业负责人（签字）：</div><div className="border-b border-gray-400 pb-1"></div></div>
                <div><div className="font-medium text-gray-800 mb-5">（企业公章）</div><div className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center"><span className="text-xs text-gray-400">公章位置</span></div></div>
              </div>
              <div className="text-right text-sm text-gray-600 mt-4">填报日期：{new Date().getFullYear()} 年 {new Date().getMonth() + 1} 月 {new Date().getDate()} 日</div>
            </div>
          </div>
        </div>

        {/* 底部 */}
        <div className="px-8 py-4 border-t border-gray-200 flex items-center justify-between bg-white">
          <div className="flex items-center text-sm text-gray-500">
            <i className="ri-information-line mr-1.5 text-teal-600"></i>
            {monthLabel} · 共 {monthData.rows.length} 个品种 · 数据来源：台账实时同步
          </div>
          <div className="flex items-center space-x-3">
            <button onClick={onClose} className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 text-sm font-medium whitespace-nowrap cursor-pointer">关闭</button>
            <button onClick={handleExportPDF} disabled={exportingPDF}
              className="px-8 py-2.5 bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-xl hover:shadow-lg text-sm font-medium whitespace-nowrap cursor-pointer disabled:opacity-60 flex items-center">
              {exportingPDF ? <><i className="ri-loader-4-line mr-2 animate-spin"></i>导出中...</> : <><i className="ri-file-pdf-2-line mr-2"></i>导出PDF</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
